import clsx from "clsx";

import type { ElementRow, LineRow, NoteWithAuthor } from "../../../api/types";
import { Pill, SpeechMark } from "../../../ui";
import { elementBadges, lineBadges, type Badge } from "../lib/badges";
import { describeLine, groupLines } from "../lib/lines";
import { lineKindWord } from "../lib/words";
import { LineActions } from "./LineActions";

export function Badges({ badges }: { badges: readonly Badge[] }) {
  if (badges.length === 0) return null;
  return (
    <span className="badges">
      {badges.map((badge) => (
        <Pill key={badge.key} tone={badge.tone}>
          {badge.text}
        </Pill>
      ))}
    </span>
  );
}

/** The state of an element, as pills with words. */
export function ElementMarks({ element }: { element: Pick<ElementRow, "state" | "origin" | "pending" | "removal" | "not_used_by_us"> }) {
  return <Badges badges={elementBadges(element)} />;
}

/** One note, as a small speech mark with its words. Notes take no state colour. */
export function NoteItem({ note }: { note: NoteWithAuthor }) {
  const who = note.kind === "sme_note" ? "SME" : note.kind === "engineer_note" ? "Engineer" : "Comment";
  return (
    <li className="note">
      <SpeechMark label={`${who} note`} />
      <div>
        <p className="note__text">{note.text}</p>
        <p className="note__by muted">
          {who} {note.author.display}
        </p>
      </div>
    </li>
  );
}

/**
 * The lines of an element grouped by kind, each with its own marks. With the element the lines hang on, an engineer also
 * gets the buttons of each line (CP-12): take it out, mark it for removal, take the mark off, drop my change.
 */
export function LineList({
  lines,
  className,
  headings = true,
  element,
}: {
  lines: readonly LineRow[];
  className?: string;
  headings?: boolean;
  element?: Pick<ElementRow, "state" | "origin">;
}) {
  if (lines.length === 0) return null;
  return (
    <div className={clsx("line-groups", className)}>
      {groupLines(lines).map((group) => (
        <section key={group.kind} className="line-group" aria-label={lineKindWord(group.kind)}>
          {headings && <h3>{lineKindWord(group.kind)}</h3>}
          <ul className="line-list">
            {group.lines.map((line) => (
              <LineItem key={line.id} line={line} element={element} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function LineItem({ line, element }: { line: LineRow; element?: Pick<ElementRow, "state" | "origin"> }) {
  const words = describeLine(line);
  const badges = lineBadges(line);
  return (
    <li className="line-item" data-line-state={line.state ?? (line.origin === "loaded" ? "loaded" : undefined)}>
      <div className="line-item__head">
        <span className="line-item__title">{words.title}</span>
        <Badges badges={badges} />
        {(line.sme_note || line.engineer_note) && <SpeechMark label="This line has a note" />}
      </div>
      {words.facts.length > 0 && (
        <ul className="line-item__facts muted">
          {words.facts.map((fact, index) => (
            <li key={index}>{fact}</li>
          ))}
        </ul>
      )}
      {words.flags.length > 0 && (
        <p className="line-item__flags">
          {words.flags.map((flag) => (
            <Pill key={flag}>{flag}</Pill>
          ))}
        </p>
      )}
      {line.sme_comment && line.state === "sent_back" && <p className="line-item__comment">The SME sent this back: {line.sme_comment}</p>}
      {line.sme_reason && line.state === "rejected" && <p className="line-item__comment">The SME rejected this: {line.sme_reason}</p>}
      {line.removal && (
        <p className="line-item__note muted">
          Why it is marked: {line.removal.reason}
          {line.removal.status === "sent_back" && line.removal.sme_comment ? `. The SME sent the removal back: ${line.removal.sme_comment}` : ""}
        </p>
      )}
      {element && <LineActions line={line} element={element} />}
    </li>
  );
}
